import { TestId } from "#/test-ids";

import "#legacy/utilities/generate_uuid.js";
import "./spotlight.js";
import "./spotlight_search/result_highlighter.js";
// jquery-ui's UMD wrapper reads the `jQuery` global at module-load time.
import "#/legacy/jquery-global.js";
// jquery-ui/ui/position depends on jquery-ui/ui/version to set up `$.ui`,
// but Vite's optimize pre-bundler does not inline that dependency for the
// UMD wrapper's non-AMD branch. Import version explicitly so `$.ui` exists
// when position runs.
import "jquery-ui/ui/version";
import "jquery-ui/ui/position";
import "#legacy/utilities/event_originated_from.js";

window.Sparkle ??= {};

const __PriorSpotlightSearch = Sparkle.SpotlightSearch;

Sparkle.SpotlightSearch = class SpotlightSearch {
  constructor(endpointUrl, options) {
    this.handleInputInteraction = this.handleInputInteraction.bind(this);
    this.handleClearButtonPressed = this.handleClearButtonPressed.bind(this);
    this.handleUpPressed = this.handleUpPressed.bind(this);
    this.handleDownPressed = this.handleDownPressed.bind(this);
    this.handleEscPressed = this.handleEscPressed.bind(this);
    this.handleEnterPressed = this.handleEnterPressed.bind(this);
    this.handleActivedescendantChanged = this.handleActivedescendantChanged.bind(this);
    this.handleItemSelected = this.handleItemSelected.bind(this);
    this.handleExternalTriggerClick = this.handleExternalTriggerClick.bind(this);
    this.closeOnInteractWithOther = this.closeOnInteractWithOther.bind(this);
    this.render = this.render.bind(this);
    this.refresh = this.refresh.bind(this);
    this.setEndpoint = this.setEndpoint.bind(this);
    this.clear = this.clear.bind(this);
    this.focus = this.focus.bind(this);
    this.updateClearButton = this.updateClearButton.bind(this);
    this.updateResults = this.updateResults.bind(this);
    this.hideResults = this.hideResults.bind(this);
    this.showResults = this.showResults.bind(this);
    this.positionResults = this.positionResults.bind(this);
    this.getQuery = this.getQuery.bind(this);
    this.setQuery = this.setQuery.bind(this);
    this.setInputValue = this.setInputValue.bind(this);
    this._fetch = this._fetch.bind(this);
    this._renderResults = this._renderResults.bind(this);
    this._highlightResults = this._highlightResults.bind(this);
    this._invalidatePendingRequests = this._invalidatePendingRequests.bind(this);
    this._generateSpotlightStatus = this._generateSpotlightStatus.bind(this);
    this._setKeyHandler = this._setKeyHandler.bind(this);
    this._triggerEvent = this._triggerEvent.bind(this);
    this._shouldAutoSelectFocussedItem = this._shouldAutoSelectFocussedItem.bind(this);
    this._eventOriginatedFrom = this._eventOriginatedFrom.bind(this);
    this._shouldIgnoreFocusInEvent = this._shouldIgnoreFocusInEvent.bind(this);
    this.endpointUrl = endpointUrl;
    if (options == null) {
      options = {};
    }
    this.input = jQuery(
      options.input || jQuery("<input>", { type: "text", class: "spotlight-search-input", placeholder: "Search term" }),
    );
    this.options = jQuery.extend(
      {
        spotlightId: Sparkle.Utilities.generateUUID(),
        minQueryLength: 2,
        minPhraseLength: 2, // Match shortest length of sphinx token
        anchor: this.input,
        identifier: true,
        autofocus: false, // Automatically focus the first item when the list results change
        autofocusIfQuery: false, // Automatically focus the first item if a query is present when the list results change. Overrides options.autofocus if given.
        autoSelectFocussedItemOnHide: false, // Automatically select the focussed item when the spotlight is hidden. Can be a boolean, or a function which receives the selected payload as an argument and returns a boolean.
        autoSelectWithoutQuery: false, // Can autoselection can occur without any text in the input element
        position: true, // Automatically position results under the input
        matchWidth: false, // Match the results dropdown to the width of the input
        spotlightClass: Sparkle.Spotlight, // Override the class used for the spotlight
        listClass: undefined, // Override the class used for the list
        itemClass: undefined, // Override the class used for the items in the list
        externalTriggers: undefined, // Other elements that trigger the focusing input, and subsequent showing of results
        clearButton: false, // Show a button that clears the input
        highlightMatchingPortions: true, // Highlight the portions of the text that matched
        hideOnEnterWithoutSelection: true, // Hide the results dropdown when enter is pressed if no selection is made as a result. Useful for closing the results if the current text will be submitted without picking a suggestion.
        hideOnSelect: true, // Hide the results dropdown once a selection is made. Set false where selecting loads a new page, so the results stay up until it arrives.
        tabbable: false, // Allow tabbing through spotlight items
        fetchOnFocus: true, // Fetch results immediately when focussing the input
        alwaysShowResults: false, // Show results even when not focussed
        inputName: undefined, // Sets the name of the input element. Useful if the input is being generated instead of passed in will be used in a form.
        onSelect: undefined, // A function which receives the selected payload as an argument when a selection is made.
      },
      options,
    );

    this.input.attr({ name: this.options.inputName });

    this.el = jQuery("<div>", { class: "spotlight-search" });
    this.el.addClass(this.options.className);
    this.spotlight = new this.options.spotlightClass({
      spotlightId: this.options.spotlightId,
      listClass: this.options.listClass,
      itemClass: this.options.itemClass,
      details: this.options.details,
      status: this.options.status,
      identifier: this.options.identifier,
      unfocus: this.options.unfocus,
      autofocus: this.options.autofocusIfQuery ? this.getQuery : this.options.autofocus,
      showOnInit: false,
      tabbable: this.options.tabbable,
    });
    this.fetchRequestId = 0;
    this.fetchResponseId = 0;
    this.clearButton = jQuery("<span>", { class: "clear-button" });
    if (!this.input.attr("data-testid")) {
      this.input.attr("data-testid", TestId.spotlight.input);
    }
    this.input.attr({
      role: "combobox",
      "aria-controls": this.options.spotlightId,
      "aria-autocomplete": "list",
      "aria-expanded": false,
      "aria-haspopup": "listbox",
    });

    // BEHAVIOUR

    this.input.on("input", this.handleInputInteraction);
    if (this.options.fetchOnFocus) {
      this.input.on("focus", this.handleInputInteraction);
    }
    if (this.options.externalTriggers != null) {
      jQuery(document).on("click", this.options.externalTriggers, this.handleExternalTriggerClick);
    }
    jQuery(document).on("click focusin", this.closeOnInteractWithOther);
    jQuery(this.spotlight).on("select", this.handleItemSelected);
    jQuery(this.spotlight).on("activedescendantChanged", this.handleActivedescendantChanged);
    this.clearButton.on("click", this.handleClearButtonPressed);
    this._setKeyHandler(this.input, 38, this.handleUpPressed); // up
    this._setKeyHandler(this.input, 40, this.handleDownPressed); // down
    this._setKeyHandler(window, 27, this.handleEscPressed); // escape
    this._setKeyHandler(this.input, 13, this.handleEnterPressed); // enter

    // INITIALIZE

    if (this.options.alwaysShowResults) {
      this.updateResults();
    }
  }

  // EVENT HANDLERS

  handleInputInteraction(event) {
    this.updateClearButton();
    return this.updateResults();
  }

  handleClearButtonPressed(event) {
    return this.clear();
  }

  handleUpPressed(event) {
    if (this.spotlight.showing) {
      return this._invalidatePendingRequests(); // Don't change the results if the user has started
    }
  }

  handleDownPressed(event) {
    if (this.spotlight.showing) {
      return this._invalidatePendingRequests(); // Don't change the results if the user has started
    } else {
      this.updateResults();
      return event.preventDefault();
    }
  }

  handleEscPressed(event) {
    if (!this.spotlight.showing) {
      return;
    }
    this.hideResults();
    return event.preventDefault();
  }

  handleEnterPressed(event) {
    if (!this.spotlight.showing || !!this.spotlight.getFocussed()) {
      return;
    }
    if (!this.options.hideOnEnterWithoutSelection) {
      return;
    }
    return this.hideResults();
  }

  // An id is worth reporting only while the results are up, but "nothing is selected" is worth
  // reporting either way: hiding the results leaves the input pointing at a row that is gone.
  handleActivedescendantChanged(event, activeId) {
    if (!(this.spotlight.showing || !activeId)) {
      return;
    }
    return this.input.attr("aria-activedescendant", activeId);
  }

  handleItemSelected(event, ...payload) {
    // Clearing the focus stops the autoselect in `hideResults` from selecting this item a second time.
    if (this.options.hideOnSelect) {
      this.spotlight.clearFocus();
    }
    this._triggerEvent("select", ...Array.from(payload));
    if (this.options.onSelect) {
      this.options.onSelect(...Array.from(payload || []));
    }
    if (this.options.hideOnSelect) {
      return this.hideResults();
    }
  }

  handleExternalTriggerClick(event) {
    return this.input.focus();
  }

  closeOnInteractWithOther(event) {
    if (
      !this._eventOriginatedFrom(event, this.spotlight.el) &&
      !this._eventOriginatedFrom(event, this.input) &&
      !this._eventOriginatedFrom(event, this.options.externalTriggers) &&
      !this._shouldIgnoreFocusInEvent(event)
    ) {
      return this.hideResults();
    }
  }

  // RENDERING

  render() {
    if (this.options.input) {
      this.el.insertAfter(this.input);
    } else {
      this.el.append(this.input);
    }

    this.el.append(this.spotlight.render().el);

    if (this.options.clearButton) {
      this.clearButton.insertAfter(this.input);
      this.updateClearButton();
    }

    return this;
  }

  // Use this if the input value was updated or the data was updated dynamically
  // It will fetch and render the content again.
  refresh() {
    return this._fetch();
  }

  // HELPER FUNCTIONS

  setEndpoint(endpointUrl) {
    this.endpointUrl = endpointUrl;
    return (this.query = null); // The same query as the last should now trigger a request because of the changed endpoint
  }

  clear() {
    this.setQuery("");
    this.updateResults();
    return this._triggerEvent("clear");
  }

  focus() {
    return this.input.focus();
  }

  updateClearButton() {
    if (this.getQuery()) {
      return this.clearButton.show();
    } else {
      return this.clearButton.hide();
    }
  }

  updateResults() {
    const newQuery = this.getQuery();

    if (this.query === newQuery) {
      this.showResults();
      return;
    }

    this.query = newQuery;

    if (!this.endpointUrl || this.query.length < this.options.minQueryLength) {
      return this.hideResults();
    } else {
      return this._fetch();
    }
  }

  hideResults() {
    if (this.options.alwaysShowResults) {
      return;
    }
    if (!this.spotlight.showing) {
      return;
    } // Avoid infinite loop if we autoselect a focussed item
    this.input.attr("aria-expanded", false);
    this._triggerEvent("hide");
    this.spotlight.hide();
    this._invalidatePendingRequests();

    if (this._shouldAutoSelectFocussedItem()) {
      return this.spotlight.selectFocussed();
    }
  }

  showResults() {
    if (this.spotlight.isEmpty) {
      return;
    }
    if (this.spotlight.showing) {
      return;
    }

    this.input.attr("aria-expanded", true);
    this.spotlight.show();
    if (this.options.position) {
      return this.positionResults();
    }
  }

  positionResults() {
    if (this.options.matchWidth) {
      this.spotlight.el.width(jQuery(this.options.anchor).outerWidth());
    }
    return this.spotlight.el.position({
      my: "left top",
      at: "left bottom",
      of: this.options.anchor,
      // The Retarget links and Insert Link lists always open below the input, and the popup scrolls to reach them.
      collision: this.input[0]?.matches(":is(.incoming-link-repair-modal, .link-dialog) .query_input")
        ? "flip none"
        : "flip",
    });
  }

  getQuery() {
    return jQuery.trim(this.input.val() || "");
  }

  setQuery(value) {
    this.setInputValue(value).change();
    return this.updateClearButton();
  }

  // PRIVATE

  setInputValue(value) {
    return this.input.val(value).attr("value", value != null ? value : ""); // Set the attribute so DOM Mutation observers can be notified of the change
  }

  _fetch() {
    const fetchRequestId = ++this.fetchRequestId; // Avoid out of order responses
    const fetchQuery = this.query;

    return jQuery.getJSON(this.endpointUrl, { query: fetchQuery }).done((response) => {
      if (fetchRequestId > this.fetchResponseId) {
        this.fetchResponseId = fetchRequestId;
        return this._renderResults(response.results, fetchQuery, this._generateSpotlightStatus(response));
      } else {
        return console.debug("discarding out of order response");
      }
    });
  }

  _renderResults(results, query, message) {
    if (this.options.highlightMatchingPortions) {
      this._highlightResults(results, query);
    }
    this.spotlight.setItems(results, message);

    if (this.spotlight.isEmpty) {
      this.input[0]?.setAttribute("aria-expanded", "false");
      return this.spotlight.hide({ silent: true });
    } else {
      return this.showResults();
    }
  }

  _highlightResults(results, query) {
    if (this.options.minPhraseLength > query.length) {
      return;
    }

    const highlighter = new Sparkle.SpotlightSearch.ResultHighlighter(
      query.split(/\s+/),
      ["name", "context", "identifier"],
      {
        ignoredPhraseDelimiters: "-'",
        ignoredTargetDelimiters: " --'",
        minPhraseLength: this.options.minPhraseLength,
      },
    );
    return jQuery.each(results, (_, result) => highlighter.highlight(result));
  }

  _invalidatePendingRequests() {
    return (this.fetchResponseId = this.fetchRequestId);
  }

  _generateSpotlightStatus(response) {
    return `Showing ${response.results.length} of ${response.totalCount} ${response.totalCount === 1 ? "result" : "results"}`;
  }

  _setKeyHandler(element, keycode, callback) {
    return jQuery(element).keydown((event) => {
      if (event.isDefaultPrevented()) {
        return;
      } // Someone else has handled this interaction
      if (event.which !== keycode) {
        return;
      }
      return callback(event);
    });
  }

  _triggerEvent(name, ...payload) {
    jQuery(this).triggerHandler(name, [...Array.from(payload), this, this.input]);
    return jQuery(this.input).triggerHandler(`spotlightSearch:${name}`, [...Array.from(payload), this, this.input]);
  }

  _shouldAutoSelectFocussedItem() {
    let focussed;
    if (!this.options.autoSelectFocussedItemOnHide) {
      return;
    }
    if (
      typeof this.options.autoSelectFocussedItemOnHide === "function" &&
      (focussed = this.spotlight.getFocussed()) &&
      !this.options.autoSelectFocussedItemOnHide(focussed)
    ) {
      return;
    }
    if (!this.options.autoSelectWithoutQuery && !this.getQuery()) {
      return;
    }
    return true;
  }

  _eventOriginatedFrom(event, element) {
    return Sparkle.Utilities.eventOriginatedFrom(event, element);
  }

  // We want to ignore focusin events if they are from an ancestor of the spotlight search and they have a tabindex
  // otherwise they will cause the results window to close even when clicking on results as they trigger a focusin event.
  _shouldIgnoreFocusInEvent(event) {
    if (event.type !== "focusin") {
      return;
    }
    return jQuery(this.el).closest(jQuery(event.target)).length;
  }
};

// Restore properties (e.g. ResultHighlighter) that were attached to
// Sparkle.SpotlightSearch by sub-module imports before the class
// assignment above overwrote the namespace.
Object.assign(Sparkle.SpotlightSearch, __PriorSpotlightSearch);
